import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import * as ICONS from "Components/Icons";
import * as CONSTANTS from "../constants";
import { ReminderTemplate } from "../templates";
import { TextFieldMultiline } from "Components/Base/Inputs/TextFieldMultiline";
import { DatePicker } from "Components/Base/Inputs/DatePicker";
import { Checkbox } from "Components/Base/Inputs/Checkbox";
import InputLabelHeader from "Components/Fragment/InputLabelHeader";
import config from "lib/config";
import styled from "styled-components";
import moment from "moment";
import { FormControl } from "Components/Base/Inputs/FormControl";
import { RadioButton, RadioGroup } from "Components/Base/Inputs/Radio";
import { FormControlLabel } from "Components/Base/Inputs/FormControlLabel";
import { Box } from "Components/Base/Layout/Box";

const StyledUlCheckbox = styled.ul`
  list-style: none;
  padding-left: 8px;
`;

const StyledRadioGroup = styled(RadioGroup)`
  gap: 8px;
`;

const CoworkerList = (props) => {
  let { receivers, languageContext, actions, reminder } = props;

  return (
    <StyledUlCheckbox>
      {receivers &&
        receivers.map((receiver, index) => (
          <li key={index}>
            <Checkbox
              checked={receiver.selected}
              label={` ${receiver.firstName} ${
                receiver.lastName
              } (${languageContext(receiver.role)} )`}
              onChange={(e) =>
                actions.onToggleReceiver(reminder, receiver, e.target.checked)
              }
            />
          </li>
        ))}
    </StyledUlCheckbox>
  );
};

const Reminder = (props) => {
  const { languageContext, reminder, actions, onUpdate, validation, locale } =
    props;
  const DATE_FORMAT =
    config && config.shortDateFormat && config.shortDateFormat.toUpperCase();
  const relativeReminder =
    reminder && reminder.relativeReminder
      ? reminder.relativeReminder.split("|")
      : "10|b|s";
  const isBefore = relativeReminder[1] === CONSTANTS.REMINDER_BEFORE;
  const isStart = relativeReminder[2] === CONSTANTS.REMINDER_START;
  const displayReceiversList =
    reminder && reminder.receivers && reminder.receivers.length > 0;
  const labelBefore =
    languageContext("before") && languageContext("before").toLowerCase();
  const labelAfter =
    languageContext("after") && languageContext("after").toLowerCase();
  const labelCoursestart =
    languageContext("coursestart") &&
    languageContext("coursestart").toLowerCase();
  const labelCourseend =
    languageContext("courseend") && languageContext("courseend").toLowerCase();
  const labelRelativeReminderDays =
    languageContext("courseend") && languageContext("days").toLowerCase();
  let relativeReminderInputRef = useRef(null);
  let relativeReminderSelect1Ref = useRef(null);
  let relativeReminderSelect2Ref = useRef(null);
  const inputStyle = { maxWidth: "75px" };
  const [reminderType, setReminderType] = useState(
    reminder.reminderType ?? CONSTANTS.REMINDER_TYPE_NONE,
  );

  const getRelativeReminder = () =>
    `${relativeReminderInputRef.current.value}|${relativeReminderSelect1Ref.current.value}|${relativeReminderSelect2Ref.current.value}`;

  function onUpdateReminder(type, value) {
    let updatedReminder = Object.assign({}, reminder);

    switch (type) {
      case CONSTANTS.FORM_REMINDERDATE:
        updatedReminder.reminderDate = value;
        break;
      case CONSTANTS.FORM_REMINDERTYPE:
        updatedReminder.reminderType = value;
        if (value === CONSTANTS.REMINDER_TYPE_RELATIVE) {
          updatedReminder.relativeReminder = getRelativeReminder();
        }
        setReminderType(Number(value));
        break;
      case CONSTANTS.FORM_RELATIVEREMINDER:
        updatedReminder.relativeReminder = getRelativeReminder();
        break;
      case CONSTANTS.FORM_RELATIVEREMINDERDATE:
        updatedReminder.relativeReminderDate = value;
        break;
      case CONSTANTS.FORM_EMAILADDRESSES:
        updatedReminder.emailAddresses = value.replace(/\s/g, "");
        break;
      case CONSTANTS.FORM_SUBJECT:
        updatedReminder.subject = value;
        break;
      case CONSTANTS.FORM_RECEIVERS:
        updatedReminder.reminderDate = value;
        break;
    }

    onUpdate(updatedReminder);
  }

  function getReminderDateErrorMessage() {
    if (!validation.reminderDateFormat_valid) {
      return languageContext("invalidreminderDate");
    }
    if (!validation.reminderDateIsAfterToday) {
      return languageContext("reminderDateBeforeToday");
    }
    if (!validation.reminderDateIsBeforeDeadline) {
      return languageContext("reminderDateAfterDeadline");
    }
  }
  const DaysAroundCourseSetting = () => (
    <div className="flex align-c flex-wrap">
      <input
        type="number"
        placeholder="10"
        defaultValue={relativeReminder[0]}
        style={inputStyle}
        width="75"
        ref={relativeReminderInputRef}
        onBlur={(e) =>
          onUpdateReminder(CONSTANTS.FORM_RELATIVEREMINDER, e.target.value)
        }
      />
      {labelRelativeReminderDays}
      &nbsp;&nbsp;
      <select
        ref={relativeReminderSelect1Ref}
        onChange={(e) =>
          onUpdateReminder(CONSTANTS.FORM_RELATIVEREMINDER, e.target.value)
        }
        value={isBefore ? CONSTANTS.REMINDER_BEFORE : CONSTANTS.REMINDER_AFTER}
      >
        <option value={CONSTANTS.REMINDER_BEFORE}>{labelBefore}</option>
        <option value={CONSTANTS.REMINDER_AFTER}>{labelAfter}</option>
      </select>
      &nbsp;&nbsp;
      <select
        ref={relativeReminderSelect2Ref}
        onChange={(e) =>
          onUpdateReminder(CONSTANTS.FORM_RELATIVEREMINDER, e.target.value)
        }
        value={isStart ? CONSTANTS.REMINDER_START : CONSTANTS.REMINDER_END}
      >
        <option value={CONSTANTS.REMINDER_START}>{labelCoursestart}</option>
        <option value={CONSTANTS.REMINDER_END}>{labelCourseend}</option>
      </select>
    </div>
  );

  const onUpdateReminderType = (value) =>
    onUpdateReminder(CONSTANTS.FORM_REMINDERTYPE, value);

  return (
    <>
      <h3>
        <ICONS.ALARM size={22} />
        {languageContext("sendreminder")}
      </h3>
      <div className="bgColor-6 padding-10">
        <Box ml={1} sx={{ width: 350 }}>
          <InputLabelHeader
            type="text"
            header={languageContext("subject")}
            defaultValue={reminder.subject}
            inputProps={{
              onBlur: (e) =>
                onUpdateReminder(CONSTANTS.FORM_SUBJECT, e.target.value),
            }}
            error={false}
            helperText={""}
          />
        </Box>
        <br />
        <FormControl component="fieldset">
          <StyledRadioGroup
            value={reminderType}
            onChange={(e) => onUpdateReminderType(e.target.value)}
          >
            <FormControlLabel
              key={CONSTANTS.REMINDER_TYPE_NONE}
              control={<RadioButton />}
              value={CONSTANTS.REMINDER_TYPE_NONE}
              label={languageContext("noreminder")}
            />
            <FormControlLabel
              key={CONSTANTS.REMINDER_TYPE_RELATIVE}
              control={<RadioButton />}
              value={CONSTANTS.REMINDER_TYPE_RELATIVE}
              label={<DaysAroundCourseSetting />}
            />
            <FormControlLabel
              key={CONSTANTS.REMINDER_TYPE_DATE}
              control={<RadioButton />}
              value={CONSTANTS.REMINDER_TYPE_DATE}
              label={
                <Box display="flex" alignItems="center" gap={8}>
                  <DatePicker
                    id="CalReminderDate"
                    name={languageContext("startdate")}
                    value={
                      reminder.reminderDate
                        ? moment(reminder.reminderDate).format(DATE_FORMAT)
                        : null
                    }
                    onChange={(event) =>
                      onUpdateReminder(
                        CONSTANTS.FORM_REMINDERDATE,
                        event.target.value,
                      )
                    }
                  />
                  {validation && !validation.reminderDate_valid && (
                    <span className="color-error">
                      {getReminderDateErrorMessage()}
                    </span>
                  )}
                </Box>
              }
            />
          </StyledRadioGroup>
        </FormControl>
        <div className="animated fadeInDown">
          <div>
            <h3>{languageContext("to")}</h3>
            {displayReceiversList ? (
              <CoworkerList
                receivers={reminder.receivers}
                languageContext={languageContext}
                actions={actions}
                reminder={reminder}
              />
            ) : (
              <ReceiversEmpty>
                {languageContext("reminderrecieversempty")}
              </ReceiversEmpty>
            )}
          </div>
          <TextFieldMultiline
            defaultValue={reminder.emailAddresses}
            label={languageContext("commadelimitedemailaddresses")}
            onBlur={(e) =>
              onUpdateReminder(CONSTANTS.FORM_EMAILADDRESSES, e.target.value)
            }
            fullWidth
          />
          {validation && !validation.reminderEmailAddresses_valid && (
            <span className="color-error">
              {languageContext("invalidemailaddresses")}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

const ReceiversEmpty = (props) => {
  return <div>{props.children}</div>;
};

ReceiversEmpty.propTypes = {
  children: PropTypes.element,
};

Reminder.defaultProps = {
  reminder: ReminderTemplate,
  validation: {},
  actions: {},
  languageContext: () => {},
};

Reminder.propTypes = {
  reminder: PropTypes.shape({
    id: PropTypes.number.isRequired,
    checklistItemId: PropTypes.number.isRequired,
    subject: PropTypes.string.isRequired,
    reminderDate: PropTypes.string.isRequired,
    reminderType: PropTypes.oneOf(["0", "1", "2"]),
    emailAddresses: PropTypes.string.isRequired,
    receivers: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  languageContext: PropTypes.func.isRequired,
  validation: PropTypes.object,
  onUpdate: PropTypes.func,
  actions: PropTypes.object,
};

export default Reminder;
